import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@project/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@project/components/ui/select";
import { Button } from "@project/components/ui/button";
import { Input } from "@project/components/ui/input";
import { Label } from "@project/components/ui/label";
import { Textarea } from "@project/components/ui/textarea";
import { Badge } from "@project/components/ui/badge";
import { Checkbox } from "@project/components/ui/checkbox";
import {
  UtensilsCrossed,
  Send,
  CheckCircle2,
  User,
  Phone,
  Calendar,
  MapPin,
  HeartHandshake,
  Users,
  Plus,
  Trash2,
  Upload,
  FileCheck,
  ShieldAlert,
  CreditCard,
  DollarSign,
  ArrowLeft,
  X,
} from "lucide-react";
import { projectToast } from "@project/lib/toast";
import { useI18n } from "@project/lib/use-i18n";
import { openRazorpayCheckout } from "@project/donate/components/razorpay-checkout";
import {
  PaymentVerificationOverlay,
  type PaymentVerificationStatus,
} from "@project/donate/components/payment-verification-overlay";
import { axiosConfig } from "@project/services/shared/api-client";
import { JIVANDHARA_BASE } from "@project/services/admin/list-api-helpers";

export interface FamilyMember {
  id: string;
  name: string;
  age: string;
  relationship: string;
  occupation: string;
  mobile: string;
}

export type AgeCategory = "above_60" | "below_60";

const getInitialStartDate = () => {
  const now = new Date();
  const currentHour = now.getHours();
  // If it's 6:00 PM (18:00) or later, start date cannot be today
  if (currentHour >= 18) {
    const tomorrow = new Date();
    tomorrow.setDate(now.getDate() + 1);
    return tomorrow.toISOString().split("T")[0];
  }
  return now.toISOString().split("T")[0];
};

export interface TiffinSevaInquiryModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  defaultCategory?: AgeCategory;
}

export function TiffinSevaInquiryModal({
  open,
  onOpenChange,
  defaultCategory,
}: TiffinSevaInquiryModalProps) {
  const { t } = useI18n();
  // Step 1: Category Selection ("category"), Step 2: Form ("form"), Step 3: Success ("submitted")
  const [step, setStep] = useState<"category" | "form" | "submitted">(
    defaultCategory ? "form" : "category"
  );
  const [ageCategory, setAgeCategory] = useState<AgeCategory>(
    defaultCategory || "above_60"
  );
  const [busy, setBusy] = useState(false);
  const [verificationOpen, setVerificationOpen] = useState(false);
  const [verificationStatus, setVerificationStatus] =
    useState<PaymentVerificationStatus>("idle");
  const [paymentDetails, setPaymentDetails] = useState({
    paymentId: "",
    status: "",
  });


  useEffect(() => {
    if (open) {
      if (step !== "submitted") {
        setStep(defaultCategory ? "form" : "category");
        setAgeCategory(defaultCategory || "above_60");
      }
    }
  }, [open, defaultCategory]);

  // Form State
  const [applicantDetails, setApplicantDetails] = useState({
    startDate: getInitialStartDate(),
    endDate: "",
    name: "",
    address: "",
    age: "",
    gender: "Male" as "Male" | "Female" | "Other",
    dob: "",
    mobile: "",
  });

  const [tiffinType, setTiffinType] = useState<string>("Daily");

  // Dynamic Family Members State
  const [familyMembers, setFamilyMembers] = useState<FamilyMember[]>([
    {
      id: "fam-1",
      name: "",
      age: "",
      relationship: "",
      occupation: "",
      mobile: "",
    },
  ]);

  // Financial & Other Details
  const [financialDetails, setFinancialDetails] = useState({
    monthlyIncome: "",
    referenceName: "",
    referenceMobile: "",
    referenceAddress: "",
    referenceOccupation: "",
    tiffinArrangement: "Lunch & Dinner Delivery (Fresh Satvik Meals)",
    tiffinRecipient: "Self",
    reasonForTiffin: "",
  });

  // Proof of Identity
  const [idProofs, setIdProofs] = useState({
    aadhaar: true,
    rationCard: false,
    voterId: false,
  });

  const [uploadedFile, setUploadedFile] = useState<File | null>(null);

  // Handlers for Family Members Table
  const handleAddFamilyMember = () => {
    setFamilyMembers((prev) => [
      ...prev,
      {
        id: `fam-${Date.now()}`,
        name: "",
        age: "",
        relationship: "",
        occupation: "",
        mobile: "",
      },
    ]);
  };

  const handleRemoveFamilyMember = (id: string) => {
    if (familyMembers.length === 1) {
      setFamilyMembers([
        {
          id: `fam-${Date.now()}`,
          name: "",
          age: "",
          relationship: "",
          occupation: "",
          mobile: "",
        },
      ]);
      return;
    }
    setFamilyMembers((prev) => prev.filter((m) => m.id !== id));
  };

  const handleUpdateFamilyMember = (
    id: string,
    field: keyof FamilyMember,
    value: string
  ) => {
    setFamilyMembers((prev) =>
      prev.map((m) => (m.id === id ? { ...m, [field]: value } : m))
    );
  };

  const handleSelectCategory = (category: AgeCategory) => {
    setAgeCategory(category);
    setApplicantDetails((prev) => ({
      ...prev,
      age:
        category === "above_60"
          ? prev.age && Number(prev.age) >= 60
            ? prev.age
            : "65"
          : prev.age && Number(prev.age) < 60
            ? prev.age
            : "45",
    }));
    setStep("form");
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!applicantDetails.name.trim()) {
      projectToast.error(t("programs.tiffinSeva.modal.errors.nameRequired"));
      return;
    }
    if (!applicantDetails.mobile.trim()) {
      projectToast.error(t("programs.tiffinSeva.modal.errors.mobileRequired"));
      return;
    }
    if (!applicantDetails.address.trim()) {
      projectToast.error(t("programs.tiffinSeva.modal.errors.addressRequired"));
      return;
    }

    const minDate = getInitialStartDate();
    if (!applicantDetails.startDate || applicantDetails.startDate < minDate) {
      const isPastSix = new Date().getHours() >= 18;
      projectToast.error(
        isPastSix
          ? "Same-day request cut-off (6:00 PM) has passed. Please select tomorrow's date or later."
          : "Please select a valid start date starting from today."
      );
      return;
    }

    setBusy(true);
    try {
      const checkedDocs: string[] = [];
      if (idProofs.aadhaar) checkedDocs.push("Aadhaar Card");
      if (idProofs.rationCard) checkedDocs.push("Ration Card");
      if (idProofs.voterId) checkedDocs.push("Voter ID");
      const identityDocsStr = checkedDocs.join(", ");

      const formattedFamilyMembers = familyMembers
        .filter((m) => m.name.trim() !== "")
        .map((m) => ({
          name: m.name,
          age: m.age ? parseInt(m.age, 10) : null,
          relationship: m.relationship,
          occupation: m.occupation,
          mobile_number: m.mobile,
        }));

      const formData = new FormData();
      formData.append("tiffin_start_date", applicantDetails.startDate);
      formData.append("tiffin_end_date", applicantDetails.endDate || "");
      formData.append("name", applicantDetails.name);
      formData.append("address", applicantDetails.address);
      formData.append("age", applicantDetails.age);
      formData.append("gender", applicantDetails.gender);
      formData.append("date_of_birth", applicantDetails.dob || "");
      formData.append("mobile_number", applicantDetails.mobile);
      formData.append("identity_documents", identityDocsStr);

      if (uploadedFile) {
        formData.append("identity_document", uploadedFile);
      }

      if (ageCategory === "above_60") {
        formData.append("monthly_income", financialDetails.monthlyIncome || "");
        formData.append("tiffin_recipient", financialDetails.tiffinRecipient || "");
        formData.append("reference_provider_name", financialDetails.referenceName || "");
        formData.append("reference_provider_mobile", financialDetails.referenceMobile || "");
        formData.append("reference_provider_address", financialDetails.referenceAddress || "");
        formData.append("reference_provider_occupation", financialDetails.referenceOccupation || "");
        formData.append("tiffin_arrangement_details", financialDetails.tiffinArrangement || "");
        formData.append("reason_for_providing_tiffin", financialDetails.reasonForTiffin || "");
        formData.append("family_members", JSON.stringify(formattedFamilyMembers));
      } else {
        formData.append("tiffin_type", tiffinType);
        formData.append("monthly_income", "");
        formData.append("tiffin_recipient", "");
        formData.append("reference_provider_name", "");
        formData.append("reference_provider_mobile", "");
        formData.append("reference_provider_address", "");
        formData.append("reference_provider_occupation", "");
        formData.append("tiffin_arrangement_details", "");
        formData.append("reason_for_providing_tiffin", "");
        formData.append("family_members", JSON.stringify([]));
      }
      formData.append("categorys", ageCategory === "above_60" ? "Above 60 Years" : "Below 60 Years");

      const response = await axiosConfig.post(
        `${JIVANDHARA_BASE}/tiffin-seva/`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );

      const resData = response.data;

      if (!resData || !resData.success) {
        projectToast.error(
          resData?.message || t("programs.tiffinSeva.modal.errors.submitError")
        );
        return;
      }

      if (ageCategory === "above_60") {
        projectToast.success(t("programs.tiffinSeva.modal.toastSenior"));
        setStep("submitted");
      } else {
        // Below 60: PAID Category
        const orderData = resData.data;
        if (!orderData?.razorpay_order_id) {
          throw new Error("Razorpay Order ID was not returned for online checkout");
        }

        // Close the Radix Dialog BEFORE opening Razorpay so its iframe is not blocked
        onOpenChange(false);

        // Small tick so Radix finishes unmounting its overlay
        await new Promise((resolve) => window.setTimeout(resolve, 80));

        const rawAmount = orderData.amount;
        const amountInPaise = typeof rawAmount === "number"
          ? rawAmount
          : Math.round(parseFloat(rawAmount) * 100);

        await openRazorpayCheckout({
          order_id: orderData.razorpay_order_id,
          amount: amountInPaise,
          currency: orderData.currency ?? "INR",
          name: "Jivandhara Old Age Home",
          description: "Tiffin Seva Registration Fee",
          prefill: {
            name: applicantDetails.name,
            contact: applicantDetails.mobile,
          },
          onDismiss: () => {
            setBusy(false);
            // Reopen the modal so the user can try again
            onOpenChange(true);
            projectToast.error("Payment cancelled. You can try again anytime.");
          },
          onSuccess: async (payResponse) => {
            setVerificationOpen(true);
            setVerificationStatus("verifying");
            try {
              const verifyRes = await axiosConfig.post(
                `${JIVANDHARA_BASE}/tiffin-seva/payment/verify/`,
                {
                  application_id: orderData.application_id,
                  razorpay_order_id: payResponse.razorpay_order_id,
                  razorpay_payment_id: payResponse.razorpay_payment_id,
                  razorpay_signature: payResponse.razorpay_signature,
                }
              );

              if (verifyRes.data?.success) {
                setVerificationStatus("success");
                setPaymentDetails({
                  paymentId: payResponse.razorpay_payment_id,
                  status: "PAID",
                });
                projectToast.success("Payment successful. Registration complete.");
                window.setTimeout(() => {
                  setVerificationOpen(false);
                  setVerificationStatus("idle");
                  // Reopen the modal and show the success step
                  setStep("submitted");
                  onOpenChange(true);
                }, 1600);
              } else {
                throw new Error(verifyRes.data?.message || "Verification failed");
              }
            } catch (verifyError: any) {
              setVerificationOpen(false);
              setVerificationStatus("idle");
              projectToast.error(
                verifyError.message || "Payment verification failed. Please contact support."
              );
              onOpenChange(true);
            }
          },
        });
      }
    } catch (err: any) {
      console.error("Tiffin Seva submission error:", err);
      projectToast.error(t("programs.tiffinSeva.modal.errors.genericError"));
    } finally {
      // For above_60, set busy to false. For below_60, busy is managed onDismiss/onSuccess
      if (ageCategory === "above_60") {
        setBusy(false);
      }
    }
  };


  const resetForm = () => {
    setApplicantDetails({
      startDate: getInitialStartDate(),
      endDate: "",
      name: "",
      address: "",
      age: "",
      gender: "Male" as "Male" | "Female" | "Other",
      dob: "",
      mobile: "",
    });
    setFamilyMembers([
      {
        id: "fam-1",
        name: "",
        age: "",
        relationship: "",
        occupation: "",
        mobile: "",
      },
    ]);
    setFinancialDetails({
      monthlyIncome: "",
      referenceName: "",
      referenceMobile: "",
      referenceAddress: "",
      referenceOccupation: "",
      tiffinArrangement: "Lunch & Dinner Delivery (Fresh Satvik Meals)",
      tiffinRecipient: "Self",
      reasonForTiffin: "",
    });
    setIdProofs({
      aadhaar: true,
      rationCard: false,
      voterId: false,
    });
    setUploadedFile(null);
    setStep(defaultCategory ? "form" : "category");
    setAgeCategory(defaultCategory || "above_60");
    setBusy(false);
    setPaymentDetails({
      paymentId: "",
      status: "",
    });
  };

  const handleClose = () => {
    resetForm();
    onOpenChange(false);
  };

  return (
    <>
      <Dialog open={open} onOpenChange={handleClose}>
        <DialogContent className="max-h-[92vh] max-w-3xl overflow-y-auto p-4 sm:p-7 rounded-2xl">
          <DialogHeader className="border-b border-border pb-3">
            <div className="flex items-center justify-between">
              <DialogTitle className="flex items-center gap-2.5 text-xl font-bold text-foreground">
                <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-primary/10 text-primary">
                  <UtensilsCrossed className="h-5 w-5" />
                </div>
                <span>{t("programs.tiffinSeva.modal.title")}</span>
              </DialogTitle>
              {step === "form" && (
                <Badge
                  variant="secondary"
                  className={
                    ageCategory === "above_60"
                      ? "bg-emerald-500/10 text-emerald-700 font-bold border border-emerald-500/30"
                      : "bg-primary/10 text-primary font-bold border border-primary/30"
                  }
                >
                  {ageCategory === "above_60"
                    ? t("programs.tiffinSeva.modal.badgeSenior")
                    : t("programs.tiffinSeva.modal.badgeGeneral")}
                </Badge>
              )}
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              {t("programs.tiffinSeva.modal.subtitle")}
            </p>
          </DialogHeader>

          {/* STEP 1: CATEGORY SELECTION POPUP */}
          {step === "category" && (
            <div className="py-6 space-y-6">
              <div className="text-center max-w-lg mx-auto space-y-1.5">
                <h3 className="text-lg font-bold text-foreground">{t("programs.tiffinSeva.modal.categoryStep.heading")}</h3>
                <p className="text-xs text-muted-foreground">
                  {t("programs.tiffinSeva.modal.categoryStep.desc")}
                </p>
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                {/* Option 1: Above 60 Years */}
                <div
                  onClick={() => handleSelectCategory("above_60")}
                  className="group relative cursor-pointer rounded-2xl border-2 border-border p-5 transition-all duration-200 hover:border-primary hover:bg-primary/[0.03] hover:shadow-md"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-emerald-500/10 text-emerald-700 group-hover:scale-105 transition-transform">
                      <Users className="h-6 w-6" />
                    </div>
                    <Badge className="bg-emerald-600 text-white font-bold text-[11px] px-2.5 py-0.5">
                      {t("programs.tiffinSeva.modal.categoryStep.seniorBadge")}
                    </Badge>
                  </div>
                  <div className="mt-4 space-y-1.5">
                    <h4 className="text-base font-bold text-foreground group-hover:text-primary transition-colors">
                      {t("programs.tiffinSeva.modal.categoryStep.seniorTitle")}
                    </h4>
                    <p className="text-xs text-muted-foreground leading-relaxed">
                      {t("programs.tiffinSeva.modal.categoryStep.seniorDesc")}
                    </p>
                  </div>
                  <div className="mt-4 pt-3 border-t border-border flex items-center justify-between text-xs">
                    <span className="font-semibold text-emerald-700">{t("programs.tiffinSeva.modal.categoryStep.seniorFee")}</span>
                    <span className="text-[11px] text-muted-foreground font-medium">{t("programs.tiffinSeva.modal.categoryStep.seniorNote")}</span>
                  </div>
                </div>

                {/* Option 2: Below 60 Years */}
                <div
                  onClick={() => handleSelectCategory("below_60")}
                  className="group relative cursor-pointer rounded-2xl border-2 border-border p-5 transition-all duration-200 hover:border-primary hover:bg-primary/[0.03] hover:shadow-md"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-primary/10 text-primary group-hover:scale-105 transition-transform">
                      <HeartHandshake className="h-6 w-6" />
                    </div>
                    <Badge className="bg-primary text-primary-foreground font-extrabold text-[11px] px-2.5 py-0.5">
                      {t("programs.tiffinSeva.modal.categoryStep.generalBadge")}
                    </Badge>
                  </div>
                  <div className="mt-4 space-y-1.5">
                    <h4 className="text-base font-bold text-foreground group-hover:text-primary transition-colors">
                      {t("programs.tiffinSeva.modal.categoryStep.generalTitle")}
                    </h4>
                    <p className="text-xs text-muted-foreground leading-relaxed">
                      {t("programs.tiffinSeva.modal.categoryStep.generalDesc")}
                    </p>
                  </div>
                  <div className="mt-4 pt-3 border-t border-border flex items-center justify-between text-xs">
                    <span className="font-extrabold text-primary">{t("programs.tiffinSeva.modal.categoryStep.generalFee")}</span>
                    <span className="text-[11px] text-muted-foreground font-medium">{t("programs.tiffinSeva.modal.categoryStep.generalNote")}</span>
                  </div>
                </div>
              </div>

              <div className="rounded-xl border border-border/80 bg-secondary/30 p-3.5 flex items-start gap-2.5 text-xs text-muted-foreground">
                <ShieldAlert className="h-4 w-4 text-primary shrink-0 mt-0.5" />
                <span>
                  {t("programs.tiffinSeva.modal.categoryStep.qualityNote")}
                </span>
              </div>
            </div>
          )}

          {/* STEP 3: SUBMITTED SUCCESS VIEW */}
          {step === "submitted" && (
            <div className="py-8 text-center space-y-5">
              <div className="mx-auto flex h-20 w-20 items-center justify-center rounded-full bg-emerald-500/10 text-emerald-600 ring-8 ring-emerald-500/5 animate-in fade-in zoom-in duration-300">
                <CheckCircle2 className="h-10 w-10" />
              </div>

              {ageCategory === "above_60" ? (
                <div className="space-y-2 max-w-lg mx-auto">
                  <h3 className="text-2xl font-bold text-foreground tracking-tight">
                    {t("programs.tiffinSeva.modal.successStep.seniorTitle")}
                  </h3>
                  <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">
                    {t("programs.tiffinSeva.modal.successStep.thankYou")} <strong className="text-foreground">{applicantDetails.name}</strong>. {t("programs.tiffinSeva.modal.successStep.seniorDesc")} <strong className="text-foreground">{applicantDetails.mobile}</strong>{t("programs.tiffinSeva.modal.successStep.seniorDescEnd")}
                  </p>
                </div>
              ) : (
                <div className="space-y-2 max-w-lg mx-auto">
                  <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-blue-500/10 text-blue-700 font-extrabold text-xs">
                    <CreditCard className="h-3.5 w-3.5" /> {t("programs.tiffinSeva.modal.successStep.generalFeeLabel")}
                  </div>
                  <h3 className="text-2xl font-bold text-foreground tracking-tight">
                    {t("programs.tiffinSeva.modal.successStep.generalTitle")}
                  </h3>
                  <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">
                    {t("programs.tiffinSeva.modal.successStep.thankYou")} <strong className="text-foreground">{applicantDetails.name}</strong>. {t("programs.tiffinSeva.modal.successStep.generalDesc")}{t("programs.tiffinSeva.modal.successStep.generalDescEnd")}
                  </p>
                </div>
              )}

              {/* Application Summary Card */}
              <div className="rounded-2xl border border-border bg-card p-5 text-left text-xs space-y-2.5 max-w-md mx-auto shadow-xs">
                <div className="flex items-center justify-between border-b border-border pb-2">
                  <span className="font-bold text-foreground text-sm">{t("programs.tiffinSeva.modal.successStep.summaryTitle")}</span>
                  <Badge variant="outline" className="text-[10px]">
                    {ageCategory === "above_60" ? t("programs.tiffinSeva.modal.successStep.summaryAbove60") : t("programs.tiffinSeva.modal.successStep.summaryBelow60")}
                  </Badge>
                </div>
                <div className="grid grid-cols-2 gap-2 text-muted-foreground">
                  <div>
                    <span className="text-[10px] uppercase font-bold text-muted-foreground/70">{t("programs.tiffinSeva.modal.successStep.labelApplicant")}</span>
                    <p className="font-semibold text-foreground">{applicantDetails.name}</p>
                  </div>
                  <div>
                    <span className="text-[10px] uppercase font-bold text-muted-foreground/70">{t("programs.tiffinSeva.modal.successStep.labelMobile")}</span>
                    <p className="font-semibold text-foreground">{applicantDetails.mobile}</p>
                  </div>
                  <div>
                    <span className="text-[10px] uppercase font-bold text-muted-foreground/70">{t("programs.tiffinSeva.modal.successStep.labelTiffinPeriod")}</span>
                    <p className="font-medium text-foreground">
                      {applicantDetails.startDate || t("programs.tiffinSeva.modal.successStep.immediate")} {applicantDetails.endDate ? `${t("programs.tiffinSeva.modal.successStep.to")} ${applicantDetails.endDate}` : ""}
                    </p>
                  </div>
                  <div>
                    <span className="text-[10px] uppercase font-bold text-muted-foreground/70">{t("programs.tiffinSeva.modal.successStep.labelFeeStatus")}</span>
                    <p className={`font-bold ${ageCategory === "above_60" ? "text-emerald-700" : "text-blue-700"}`}>
                      {ageCategory === "above_60" ? t("programs.tiffinSeva.modal.successStep.feeFree") : t("programs.tiffinSeva.modal.successStep.feePaid")}
                    </p>
                  </div>
                  {ageCategory === "below_60" && paymentDetails.paymentId && (
                    <>
                      <div>
                        <span className="text-[10px] uppercase font-bold text-muted-foreground/70">Payment ID</span>
                        <p className="font-mono font-medium text-foreground">{paymentDetails.paymentId}</p>
                      </div>
                      <div>
                        <span className="text-[10px] uppercase font-bold text-muted-foreground/70">Payment Status</span>
                        <p className="font-bold text-emerald-700">{paymentDetails.status}</p>
                      </div>
                    </>
                  )}
                </div>
              </div>

              <div className="flex justify-center gap-3 pt-2">
                <Button
                  onClick={handleClose}
                  className="bg-primary text-primary-foreground font-semibold px-8"
                >
                  {t("programs.tiffinSeva.modal.successStep.close")}
                </Button>
              </div>
            </div>
          )}

          {/* STEP 2: FULL APPLICATION FORM */}
          {step === "form" && (
            <form onSubmit={handleSubmit} className="space-y-6 pt-2">
              {/* Category Banner & Switcher */}
              <div className="flex flex-wrap items-center justify-between gap-2 p-3.5 rounded-xl border border-border bg-secondary/30">
                <div className="flex items-center gap-2">
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    onClick={() => setStep("category")}
                    className="h-7 text-xs text-muted-foreground hover:text-foreground gap-1 px-1.5"
                  >
                    <ArrowLeft className="h-3.5 w-3.5" /> {t("programs.tiffinSeva.modal.formStep.switchCategory")}
                  </Button>
                  <span className="text-xs text-muted-foreground">|</span>
                  <span className="text-xs font-bold text-foreground">
                    {t("programs.tiffinSeva.modal.formStep.categoryLabel")} {ageCategory === "above_60" ? t("programs.tiffinSeva.modal.formStep.seniorCategoryText") : t("programs.tiffinSeva.modal.formStep.generalCategoryText")}
                  </span>
                </div>

                {ageCategory === "below_60" ? (
                  <Badge className="bg-blue-600 text-white font-extrabold text-xs px-2.5 py-0.5">
                    {t("programs.tiffinSeva.modal.formStep.registrationFeesBadge")}
                  </Badge>
                ) : (
                  <Badge className="bg-emerald-600 text-white font-bold text-xs px-2.5 py-0.5">
                    {t("programs.tiffinSeva.modal.formStep.trustSponsoredBadge")}
                  </Badge>
                )}
              </div>

              {/* SECTION 1: APPLICANT DETAILS */}
              <div className="space-y-3.5 rounded-2xl border border-border/80 bg-card p-4 sm:p-5">
                <div className="flex items-center gap-2 border-b border-border pb-2.5">
                  <span className="flex h-6 w-6 items-center justify-center rounded-full bg-primary text-primary-foreground text-xs font-bold">
                    1
                  </span>
                  <h4 className="text-sm font-bold text-foreground">{t("programs.tiffinSeva.modal.formStep.section1Title")}</h4>
                </div>

                <div className="grid gap-3.5 sm:grid-cols-2">
                  {/* Tiffin Start Date */}
                  <div>
                    <Label className="text-xs font-semibold">{t("programs.tiffinSeva.modal.formStep.tiffinStartDate")}</Label>
                    <div className="relative mt-1">
                      <Calendar className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
                      <Input
                        type="date"
                        required
                        min={getInitialStartDate()}
                        value={applicantDetails.startDate}
                        onChange={(e) =>
                          setApplicantDetails({ ...applicantDetails, startDate: e.target.value })
                        }
                        className="pl-9 text-xs"
                      />
                    </div>
                  </div>

                  {/* Tiffin End Date */}
                  <div>
                    <Label className="text-xs font-semibold">{t("programs.tiffinSeva.modal.formStep.tiffinEndDate")}</Label>
                    <div className="relative mt-1">
                      <Calendar className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
                      <Input
                        type="date"
                        value={applicantDetails.endDate}
                        onChange={(e) =>
                          setApplicantDetails({ ...applicantDetails, endDate: e.target.value })
                        }
                        className="pl-9 text-xs"
                        placeholder={t("programs.tiffinSeva.modal.formStep.endDatePlaceholder")}
                      />
                    </div>
                  </div>

                  {/* Red cut-off warning spanning full width */}
                  {new Date().getHours() >= 18 && (
                    <div className="sm:col-span-2">
                      <p className="text-xs font-bold text-rose-600 leading-snug">
                        * Same-day request cut-off is 6:00 PM. Applications after 6:00 PM must start from tomorrow.
                      </p>
                    </div>
                  )}

                  {/* Full Name */}
                  <div className="sm:col-span-2">
                    <Label className="text-xs font-semibold">{t("programs.tiffinSeva.modal.formStep.name")}</Label>
                    <div className="relative mt-1">
                      <User className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
                      <Input
                        required
                        placeholder={t("programs.tiffinSeva.modal.formStep.namePlaceholder")}
                        value={applicantDetails.name}
                        onChange={(e) =>
                          setApplicantDetails({ ...applicantDetails, name: e.target.value })
                        }
                        className="pl-9 text-xs font-medium"
                      />
                    </div>
                  </div>

                  {/* Address */}
                  <div className="sm:col-span-2">
                    <Label className="text-xs font-semibold">{t("programs.tiffinSeva.modal.formStep.address")}</Label>
                    <div className="relative mt-1">
                      <MapPin className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
                      <Textarea
                        required
                        rows={2}
                        placeholder={t("programs.tiffinSeva.modal.formStep.addressPlaceholder")}
                        value={applicantDetails.address}
                        onChange={(e) =>
                          setApplicantDetails({ ...applicantDetails, address: e.target.value })
                        }
                        className="pl-9 text-xs"
                      />
                    </div>
                  </div>

                  {/* Age */}
                  <div>
                    <Label className="text-xs font-semibold">{t("programs.tiffinSeva.modal.formStep.age")}</Label>
                    <Input
                      type="number"
                      required
                      min={ageCategory === "above_60" ? "60" : "1"}
                      max={ageCategory === "below_60" ? "59" : "120"}
                      placeholder={ageCategory === "above_60" ? t("programs.tiffinSeva.modal.formStep.agePlaceholderSenior") : t("programs.tiffinSeva.modal.formStep.agePlaceholderGeneral")}
                      value={applicantDetails.age}
                      onChange={(e) =>
                        setApplicantDetails({ ...applicantDetails, age: e.target.value })
                      }
                      className="mt-1 text-xs"
                    />
                  </div>

                  {/* Gender */}
                  <div>
                    <Label className="text-xs font-semibold" data-required="true">{t("programs.tiffinSeva.modal.formStep.gender")}</Label>
                    <div className="mt-1.5 flex items-center gap-4 text-xs font-medium">
                      <label className="flex items-center gap-1.5 cursor-pointer">
                        <input
                          type="radio"
                          name="gender"
                          value="Male"
                          checked={applicantDetails.gender === "Male"}
                          onChange={() => setApplicantDetails({ ...applicantDetails, gender: "Male" })}
                          className="text-primary focus:ring-primary h-3.5 w-3.5"
                        />
                        <span>{t("programs.tiffinSeva.modal.formStep.male")}</span>
                      </label>
                      <label className="flex items-center gap-1.5 cursor-pointer">
                        <input
                          type="radio"
                          name="gender"
                          value="Female"
                          checked={applicantDetails.gender === "Female"}
                          onChange={() => setApplicantDetails({ ...applicantDetails, gender: "Female" })}
                          className="text-primary focus:ring-primary h-3.5 w-3.5"
                        />
                        <span>{t("programs.tiffinSeva.modal.formStep.female")}</span>
                      </label>
                      <label className="flex items-center gap-1.5 cursor-pointer">
                        <input
                          type="radio"
                          name="gender"
                          value="Other"
                          checked={applicantDetails.gender === "Other"}
                          onChange={() => setApplicantDetails({ ...applicantDetails, gender: "Other" })}
                          className="text-primary focus:ring-primary h-3.5 w-3.5"
                        />
                        <span>{t("programs.tiffinSeva.modal.formStep.other")}</span>
                      </label>
                    </div>
                  </div>

                  {/* Date of Birth */}
                  <div>
                    <Label className="text-xs font-semibold">{t("programs.tiffinSeva.modal.formStep.dateOfBirth")}</Label>
                    <Input
                      type="date"
                      value={applicantDetails.dob}
                      onChange={(e) =>
                        setApplicantDetails({ ...applicantDetails, dob: e.target.value })
                      }
                      className="mt-1 text-xs"
                    />
                  </div>

                  {/* Mobile No */}
                  <div>
                    <Label className="text-xs font-semibold">{t("programs.tiffinSeva.modal.formStep.mobileNo")}</Label>
                    <div className="relative mt-1">
                      <Phone className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
                      <Input
                        type="tel"
                        required
                        placeholder="+91 98250 12345"
                        value={applicantDetails.mobile}
                        onChange={(e) =>
                          setApplicantDetails({ ...applicantDetails, mobile: e.target.value })
                        }
                        className="pl-9 text-xs font-mono"
                      />
                    </div>
                  </div>

                  {/* Tiffin Seva Type (Only for Below 60 Years) */}
                  {ageCategory === "below_60" && (
                    <div>
                      <Label className="text-xs font-semibold">Tiffin Seva Type *</Label>
                      <div className="mt-1">
                        <Select
                          value={tiffinType}
                          onValueChange={(val) => setTiffinType(val)}
                        >
                          <SelectTrigger className="text-xs h-9">
                            <SelectValue placeholder="Select Seva Type" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="Daily">Daily</SelectItem>
                            <SelectItem value="One-Time">One-Time</SelectItem>
                            <SelectItem value="Weekly">Weekly</SelectItem>
                            <SelectItem value="Monthly">Monthly</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* SECTION 2: FAMILY DETAILS */}
              {ageCategory === "above_60" && (
                <div className="space-y-3.5 rounded-2xl border border-border/80 bg-card p-4 sm:p-5">
                  <div className="flex flex-wrap items-center justify-between gap-2 border-b border-border pb-2.5">
                    <div className="flex items-center gap-2">
                      <span className="flex h-6 w-6 items-center justify-center rounded-full bg-primary text-primary-foreground text-xs font-bold">
                        2
                      </span>
                      <div>
                        <h4 className="text-sm font-bold text-foreground">{t("programs.tiffinSeva.modal.formStep.section2Title")}</h4>
                        <p className="text-[11px] text-muted-foreground">{t("programs.tiffinSeva.modal.formStep.section2Desc")}</p>
                      </div>
                    </div>
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={handleAddFamilyMember}
                      className="text-xs font-semibold gap-1 text-primary hover:text-primary hover:bg-primary/10"
                    >
                      <Plus className="h-3.5 w-3.5" /> {t("programs.tiffinSeva.modal.formStep.addFamilyMember")}
                    </Button>
                  </div>

                  {/* Dynamic Family Table */}
                  <div className="overflow-x-auto rounded-xl border border-border">
                    <table className="w-full text-left text-xs">
                      <thead className="bg-secondary/40 text-muted-foreground font-semibold border-b border-border">
                        <tr>
                          <th className="p-2.5 text-center w-14">Sr. No.</th>
                          <th className="p-2.5 min-w-[140px]">{t("programs.tiffinSeva.modal.formStep.colName")}</th>
                          <th className="p-2.5 w-20">{t("programs.tiffinSeva.modal.formStep.colAge")}</th>
                          <th className="p-2.5 min-w-[110px]">{t("programs.tiffinSeva.modal.formStep.colRelationship")}</th>
                          <th className="p-2.5 min-w-[120px]">{t("programs.tiffinSeva.modal.formStep.colOccupation")}</th>
                          <th className="p-2.5 min-w-[120px]">{t("programs.tiffinSeva.modal.formStep.colMobile")}</th>
                          <th className="p-2.5 text-center w-12">{t("programs.tiffinSeva.modal.formStep.colAction")}</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-border">
                        {familyMembers.map((member, idx) => (
                          <tr key={member.id} className="hover:bg-secondary/15 transition-colors">
                            <td className="p-2 text-center font-mono font-semibold text-muted-foreground">
                              {idx + 1}
                            </td>
                            <td className="p-2">
                              <Input
                                placeholder={t("programs.tiffinSeva.modal.formStep.colName")}
                                value={member.name}
                                onChange={(e) =>
                                  handleUpdateFamilyMember(member.id, "name", e.target.value)
                                }
                                className="h-8 text-xs"
                              />
                            </td>
                            <td className="p-2">
                              <Input
                                type="number"
                                placeholder={t("programs.tiffinSeva.modal.formStep.colAge")}
                                value={member.age}
                                onChange={(e) =>
                                  handleUpdateFamilyMember(member.id, "age", e.target.value)
                                }
                                className="h-8 text-xs"
                              />
                            </td>
                            <td className="p-2">
                              <Input
                                placeholder={t("programs.tiffinSeva.modal.formStep.colRelationship")}
                                value={member.relationship}
                                onChange={(e) =>
                                  handleUpdateFamilyMember(member.id, "relationship", e.target.value)
                                }
                                className="h-8 text-xs"
                              />
                            </td>
                            <td className="p-2">
                              <Input
                                placeholder={t("programs.tiffinSeva.modal.formStep.colOccupation")}
                                value={member.occupation}
                                onChange={(e) =>
                                  handleUpdateFamilyMember(member.id, "occupation", e.target.value)
                                }
                                className="h-8 text-xs"
                              />
                            </td>
                            <td className="p-2">
                              <Input
                                placeholder={t("programs.tiffinSeva.modal.formStep.colMobile")}
                                value={member.mobile}
                                onChange={(e) =>
                                  handleUpdateFamilyMember(member.id, "mobile", e.target.value)
                                }
                                className="h-8 text-xs"
                              />
                            </td>
                            <td className="p-2 text-center">
                              <Button
                                type="button"
                                size="icon"
                                variant="ghost"
                                onClick={() => handleRemoveFamilyMember(member.id)}
                                className="h-7 w-7 text-destructive hover:bg-destructive/10"
                                title="Remove row"
                              >
                                <Trash2 className="h-3.5 w-3.5" />
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* SECTION 3: FINANCIAL & OTHER DETAILS */}
              {ageCategory === "above_60" && (
                <div className="space-y-3.5 rounded-2xl border border-border/80 bg-card p-4 sm:p-5">
                  <div className="flex items-center gap-2 border-b border-border pb-2.5">
                    <span className="flex h-6 w-6 items-center justify-center rounded-full bg-primary text-primary-foreground text-xs font-bold">
                      3
                    </span>
                    <h4 className="text-sm font-bold text-foreground">{t("programs.tiffinSeva.modal.formStep.section3Title")}</h4>
                  </div>

                  <div className="grid gap-3.5 sm:grid-cols-2">
                    {/* Family's Monthly Income */}
                    <div>
                      <Label className="text-xs font-semibold">{t("programs.tiffinSeva.modal.formStep.monthlyIncome")}</Label>
                      <div className="relative mt-1">
                        <DollarSign className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
                        <Input
                          placeholder={t("programs.tiffinSeva.modal.formStep.monthlyIncomePlaceholder")}
                          value={financialDetails.monthlyIncome}
                          onChange={(e) =>
                            setFinancialDetails({ ...financialDetails, monthlyIncome: e.target.value })
                          }
                          className="pl-9 text-xs"
                        />
                      </div>
                    </div>

                    {/* Tiffin Recipient */}
                    <div>
                      <Label className="text-xs font-semibold">{t("programs.tiffinSeva.modal.formStep.tiffinRecipient")}</Label>
                      <Input
                        placeholder={t("programs.tiffinSeva.modal.formStep.tiffinRecipientPlaceholder")}
                        value={financialDetails.tiffinRecipient}
                        onChange={(e) =>
                          setFinancialDetails({ ...financialDetails, tiffinRecipient: e.target.value })
                        }
                        className="mt-1 text-xs"
                      />
                    </div>

                    {/* Reference Provider's Name */}
                    <div>
                      <Label className="text-xs font-semibold">{t("programs.tiffinSeva.modal.formStep.referenceName")}</Label>
                      <Input
                        placeholder={t("programs.tiffinSeva.modal.formStep.referenceNamePlaceholder")}
                        value={financialDetails.referenceName}
                        onChange={(e) =>
                          setFinancialDetails({ ...financialDetails, referenceName: e.target.value })
                        }
                        className="mt-1 text-xs"
                      />
                    </div>

                    {/* Reference Provider's Mobile No */}
                    <div>
                      <Label className="text-xs font-semibold">{t("programs.tiffinSeva.modal.formStep.referenceMobile")}</Label>
                      <Input
                        type="tel"
                        placeholder="+91 98980 12345"
                        value={financialDetails.referenceMobile}
                        onChange={(e) =>
                          setFinancialDetails({ ...financialDetails, referenceMobile: e.target.value })
                        }
                        className="mt-1 text-xs"
                      />
                    </div>

                    {/* Reference Provider's Address */}
                    <div>
                      <Label className="text-xs font-semibold">{t("programs.tiffinSeva.modal.formStep.referenceAddress")}</Label>
                      <Input
                        placeholder={t("programs.tiffinSeva.modal.formStep.referenceAddressPlaceholder")}
                        value={financialDetails.referenceAddress}
                        onChange={(e) =>
                          setFinancialDetails({ ...financialDetails, referenceAddress: e.target.value })
                        }
                        className="mt-1 text-xs"
                      />
                    </div>

                    {/* Reference Provider's Occupation */}
                    <div>
                      <Label className="text-xs font-semibold">{t("programs.tiffinSeva.modal.formStep.referenceOccupation")}</Label>
                      <Input
                        placeholder={t("programs.tiffinSeva.modal.formStep.referenceOccupationPlaceholder")}
                        value={financialDetails.referenceOccupation}
                        onChange={(e) =>
                          setFinancialDetails({ ...financialDetails, referenceOccupation: e.target.value })
                        }
                        className="mt-1 text-xs"
                      />
                    </div>

                    {/* Tiffin Arrangement Details */}
                    <div className="sm:col-span-2">
                      <Label className="text-xs font-semibold">{t("programs.tiffinSeva.modal.formStep.tiffinArrangement")}</Label>
                      <Input
                        placeholder={t("programs.tiffinSeva.modal.formStep.tiffinArrangementPlaceholder")}
                        value={financialDetails.tiffinArrangement}
                        onChange={(e) =>
                          setFinancialDetails({ ...financialDetails, tiffinArrangement: e.target.value })
                        }
                        className="mt-1 text-xs"
                      />
                    </div>

                    {/* Reason for Providing Tiffin */}
                    <div className="sm:col-span-2">
                      <Label className="text-xs font-semibold">{t("programs.tiffinSeva.modal.formStep.reasonForTiffin")}</Label>
                      <Textarea
                        required
                        rows={2}
                        placeholder={t("programs.tiffinSeva.modal.formStep.reasonPlaceholder")}
                        value={financialDetails.reasonForTiffin}
                        onChange={(e) =>
                          setFinancialDetails({ ...financialDetails, reasonForTiffin: e.target.value })
                        }
                        className="mt-1 text-xs"
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* SECTION 4: PROOF OF IDENTITY */}
              <div className="space-y-3.5 rounded-2xl border border-border/80 bg-card p-4 sm:p-5">
                <div className="flex items-center gap-2 border-b border-border pb-2.5">
                  <span className="flex h-6 w-6 items-center justify-center rounded-full bg-primary text-primary-foreground text-xs font-bold">
                    {ageCategory === "above_60" ? "4" : "2"}
                  </span>
                  <h4 className="text-sm font-bold text-foreground">{t("programs.tiffinSeva.modal.formStep.section4Title")}</h4>
                </div>

                {/* Checkboxes */}
                <div className="space-y-2">
                  <Label className="text-xs font-semibold text-muted-foreground">{t("programs.tiffinSeva.modal.formStep.selectIdDocs")}</Label>
                  <div className="flex flex-wrap gap-4 pt-1">
                    <label className="flex items-center gap-2 cursor-pointer text-xs font-medium text-foreground">
                      <Checkbox
                        checked={idProofs.aadhaar}
                        onCheckedChange={(checked) =>
                          setIdProofs({ ...idProofs, aadhaar: Boolean(checked) })
                        }
                      />
                      <span>{t("programs.tiffinSeva.modal.formStep.aadhaar")}</span>
                    </label>

                    <label className="flex items-center gap-2 cursor-pointer text-xs font-medium text-foreground">
                      <Checkbox
                        checked={idProofs.rationCard}
                        onCheckedChange={(checked) =>
                          setIdProofs({ ...idProofs, rationCard: Boolean(checked) })
                        }
                      />
                      <span>{t("programs.tiffinSeva.modal.formStep.rationCard")}</span>
                    </label>

                    <label className="flex items-center gap-2 cursor-pointer text-xs font-medium text-foreground">
                      <Checkbox
                        checked={idProofs.voterId}
                        onCheckedChange={(checked) =>
                          setIdProofs({ ...idProofs, voterId: Boolean(checked) })
                        }
                      />
                      <span>{t("programs.tiffinSeva.modal.formStep.voterId")}</span>
                    </label>
                  </div>
                </div>

                {/* Upload Identity Option */}
                <div className="pt-2">
                  <Label className="text-xs font-semibold">{t("programs.tiffinSeva.modal.formStep.uploadLabel")}</Label>
                  <div className="mt-1.5 flex flex-col sm:flex-row items-center gap-3">
                    <label className="flex flex-1 w-full cursor-pointer items-center justify-center gap-2 rounded-xl border-2 border-dashed border-border px-4 py-3 text-xs font-medium text-muted-foreground hover:border-primary/60 hover:bg-primary/[0.02] transition-colors">
                      <Upload className="h-4 w-4 text-primary" />
                      <span>{uploadedFile ? t("programs.tiffinSeva.modal.formStep.changeDoc") : t("programs.tiffinSeva.modal.formStep.uploadPrompt")}</span>
                      <input
                        type="file"
                        accept=".pdf,.jpg,.jpeg,.png"
                        className="hidden"
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) {
                            setUploadedFile(file);
                            projectToast.success(`Uploaded: ${file.name}`);
                          }
                        }}
                      />
                    </label>

                    {uploadedFile && (
                      <div className="flex items-center gap-2 rounded-xl border border-emerald-500/30 bg-emerald-500/10 px-3 py-2 text-xs text-emerald-800">
                        <FileCheck className="h-4 w-4 text-emerald-600 shrink-0" />
                        <span className="truncate max-w-[150px] font-medium">{uploadedFile.name}</span>
                        <Button
                          type="button"
                          size="icon"
                          variant="ghost"
                          onClick={() => setUploadedFile(null)}
                          className="h-5 w-5 text-destructive hover:bg-destructive/10"
                        >
                          <X className="h-3 w-3" />
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Footer Buttons */}
              <div className="flex flex-wrap items-center justify-between gap-3 pt-3 border-t border-border">
                <Button type="button" variant="outline" onClick={() => setStep("category")}>
                  {t("programs.tiffinSeva.modal.formStep.back")}
                </Button>
                <div className="flex items-center gap-2">
                  <Button type="button" variant="ghost" onClick={handleClose}>
                    {t("programs.tiffinSeva.modal.formStep.cancel")}
                  </Button>
                  <Button
                    type="submit"
                    disabled={busy}
                    className="bg-primary text-primary-foreground font-semibold gap-1.5 shadow-sm"
                  >
                    {busy ? (
                      <span className="flex items-center gap-1.5">
                        {t("programs.tiffinSeva.modal.formStep.submitting")}
                      </span>
                    ) : (
                      <>
                        <Send className="h-4 w-4" />
                        {ageCategory === "below_60"
                          ? t("programs.tiffinSeva.modal.formStep.submitGeneral")
                          : t("programs.tiffinSeva.modal.formStep.submitSenior")}
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>

      <PaymentVerificationOverlay
        open={verificationOpen}
        status={verificationStatus}
      />
    </>
  );
}
